import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Grid,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { NewUserType } from 'types/new-user';

import { ContactsDisplayMode } from '../hooks/use-contacts-variant';
import EmptyContainer from './components/empty-container';
import GridResults from './components/grid';
import Header from './components/header';
import List from './components/list';
import { ModalCreateContact } from './components/modal-create-contact/modal-create-contact';
import ResultsSkeleton from './components/results-skeleton';
import SearchField from './components/search-field';
import { ColumnsType } from './services';
import { SearchState } from './services/search-state';
import { ContactKind } from './types';

const useStyles = makeStyles((theme: any) => ({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: { padding: theme.spacing(3) },
  },
  mainContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(3),
    },
  },
  btnAddContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: { marginTop: theme.spacing(3) },
  },
  button: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.secondary.main,
    textTransform: 'none',
    [theme.breakpoints.down('sm')]: { width: '100%' },
    '@media (hover: none) and (pointer: coarse)': { minHeight: theme.spacing(5.5) },
  },
  icon: {
    fontSize: 16,
    marginRight: theme.spacing(1),
  },
  resultsContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: { marginTop: theme.spacing(3) },
  },
  // Reserved slot so the refetch indicator never shifts the results.
  progressSlot: {
    height: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  status: {
    textAlign: 'center',
    color: theme.palette.text.shadow,
    marginBottom: theme.spacing(2),
  },
}));

type ContactsSceneType = {
  t: TFunction;
  columns: ColumnsType;
  parties: NewUserType[];
  displayMode: ContactsDisplayMode;
  canToggleDisplayMode: boolean;
  searchText: string | undefined;
  searchState: SearchState;
  isFetching: boolean;
  createModalOpen: boolean;
  dialogFullScreen: boolean;
  handleDisplayMode: (mode: ContactsDisplayMode) => void;
  handleSearch: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  handleEditNav: (id: string) => void;
  handleUserCreateModal: (state: boolean, kind: ContactKind) => void;
  contactKind: ContactKind
};

const ContactsScene: FC<ContactsSceneType> = ({
  t,
  columns,
  parties,
  displayMode,
  canToggleDisplayMode,
  searchText,
  searchState,
  isFetching,
  createModalOpen,
  dialogFullScreen,
  handleDisplayMode,
  handleSearch,
  handleEditNav,
  handleUserCreateModal,
  contactKind
}) => {
  const classes = useStyles();

  return (
    <>
      <Grid container direction="column" className={classes.root}>
        <Grid item xs={12}>
          <Header
            t={t}
            displayMode={displayMode}
            canToggleDisplayMode={canToggleDisplayMode}
            handleDisplayMode={handleDisplayMode}
          />
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <Grid container className={classes.mainContainer}>
              <Grid item xs={12}>
                <SearchField
                  t={t}
                  searchText={searchText}
                  handleSearch={handleSearch}
                />
              </Grid>

              {searchState === 'results' && (
                <Grid item className={classes.btnAddContainer}>
                  <Button
                    disableElevation
                    variant="outlined"
                    color="secondary"
                    className={classes.button}
                    onClick={() => handleUserCreateModal(true, 'Person')}
                  >
                    <FontAwesomeIcon icon="plus" className={classes.icon} />
                    {t('users:create-new')}
                  </Button>
                </Grid>
              )}

              {(searchState === 'idle' || searchState === 'empty' || searchState === 'error') && (
                <Grid item xs={12}>
                  <EmptyContainer
                    t={t}
                    searchState={searchState}
                    searchText={searchText}
                    handleUserCreateModal={handleUserCreateModal}
                  />
                </Grid>
              )}

              {searchState === 'loading' && (
                <Grid item xs={12} className={classes.resultsContainer} aria-busy>
                  <Typography variant="body2" role="status" className={classes.status}>
                    {t('contact:searching')}
                  </Typography>
                  <ResultsSkeleton displayMode={displayMode} />
                </Grid>
              )}

              {searchState === 'results' && (
                <Grid item xs={12} className={classes.resultsContainer} aria-busy={isFetching}>
                  <div className={classes.progressSlot}>
                    {isFetching && <LinearProgress color="secondary" aria-label={t('contact:searching')} />}
                  </div>

                  {displayMode === 'list' ? (
                    <List
                      parties={parties}
                      columns={columns}
                      handleEditNav={handleEditNav}
                    />
                  ) : (
                    <GridResults
                      t={t}
                      parties={parties}
                      handleEditNav={handleEditNav}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {createModalOpen && (
        <ModalCreateContact
          searchText={searchText}
          open={createModalOpen}
          fullScreen={dialogFullScreen}
          onClose={() => handleUserCreateModal(false, 'Person')}
          contactKind={contactKind}
        />
      )}
    </>
  );
};

export default ContactsScene;
